/**  DEPRECATE THIS FUNCTION (AYC):
 *  - see getCustomResultRow.tsx instead
 *  - see more details in readme: https://dropbox.sourcegraphcloud.com/github.com/dropbox-internal/dash/-/blob/packages/mirage/packages/mosaics/Omnibox/components/ResultRow/Readme.md
 */
import { UIIcon } from "@dropbox/dig-icons";
import {
  CalculatorLine,
  ClockLine,
  EditLine,
  SearchLine,
  Twinkle2Line,
} from "@dropbox/dig-icons/assets";
import {
  getCalendarSubtitle,
  getTimeAgoStringFromTimestamp,
} from "@mirage/search/SearchResults/util/resultUtil";
import { ResultType } from "@mirage/service-typeahead-search/service/types";
import { AvatarIcon } from "@mirage/shared/icons/AvatarIcon";
import { FileContentIconWithThumbnail } from "@mirage/shared/icons/FileContentIcon";
import { stripStringOfPotentialFilterBindings } from "@mirage/shared/search/search-filters";
import i18n from "@mirage/translations";

import { IconLayout } from "@dropbox/dash-component-library";
import { Skeleton } from "@dropbox/dig-components/skeleton";
import { getTypeaheadFilterDisplayProps } from "@mirage/mosaics/SearchBarWithTypeahead/typeahead-search-filter-result";
import type { TypeaheadResult } from "@mirage/mosaics/SearchBarWithTypeahead/useConvertToTypeaheadResults";
import { getUserProfilePageHref } from "@mirage/mosaics/UserProfilePage/utils";
import { Highlighter } from "@mirage/search/SearchResults/Highlighter";
import type { SearchResult } from "@mirage/service-dbx-api";
import { tagged } from "@mirage/service-logging";
import type { typeahead } from "@mirage/service-typeahead-search/service/types";
import type { Recommendation } from "@mirage/shared/search/recommendation";

const logger = tagged("getDisplayResult");

type RowDisplayResult = Pick<TypeaheadResult, "icon" | "title" | "subtitle">;

export const isCalendarEvent = (result: typeahead.ScoredResult) => {
  return (
    result.type === ResultType.SearchResult &&
    result.result.recordType[".tag"] === "event"
  );
};

export const getModifiedTimeAgo = (result: SearchResult | Recommendation) => {
  const { providerUpdateAtMs } = result;
  if (providerUpdateAtMs) {
    return i18n.t("updated_ago", {
      timeAgo: getTimeAgoStringFromTimestamp(providerUpdateAtMs, true),
    });
  }

  return "";
};
type UnsupportedResultTypes =
  | typeof ResultType.Stack
  | typeof ResultType.StackItem
  | typeof ResultType.PreviousQuery
  | typeof ResultType.URLShortcut
  | typeof ResultType.DesktopApplication
  | typeof ResultType.DraftWithSource
  | typeof ResultType.DraftFromTemplate
  | typeof ResultType.DraftFromTone
  | typeof ResultType.OrganizeUserQuery
  | typeof ResultType.PeopleFilter
  | typeof ResultType.LoadingCompletion;

// DEPRECATE THIS FUNCTION (AYC)
// move this into ResultRowByType/getCustomResultRow.tsx
export function getDisplayResult(
  query: string,
  result: Exclude<typeahead.ScoredResult, { type: UnsupportedResultTypes }>,
): RowDisplayResult {
  switch (result.type) {
    case ResultType.AskUserQuery:
      return {
        icon: (
          <UIIcon
            src={Twinkle2Line}
            size={"medium"}
            color={"var(--dig-color__text__base)"}
          />
        ),
        title: stripStringOfPotentialFilterBindings(result.result.query),
        subtitle: i18n.t("omnibox_subtitle_ask_dash"),
      };
    case ResultType.WriteUserQuery:
      return {
        icon: (
          <UIIcon
            src={EditLine}
            size={"medium"}
            color={"var(--dig-color__text__base)"}
          />
        ),
        title: stripStringOfPotentialFilterBindings(result.result.query),
        subtitle: i18n.t("omnibox_subtitle_draft_dash"),
      };
    case ResultType.SuggestedQuery:
      return {
        icon: (
          <UIIcon
            src={SearchLine}
            size={"medium"}
            color={"var(--dig-color__text__base)"}
          />
        ),
        title: stripStringOfPotentialFilterBindings(result.result.query),
        // NOTE: we're defaulting to Find CTA for now, may need to switch on result type to generate correct subtitle
        subtitle: i18n.t("search_dash_byline"),
      };
    case ResultType.CalendarEvent:
    case ResultType.SearchResult:
    case ResultType.DesktopFile:
      return {
        icon: (
          <FileContentIconWithThumbnail
            content={result.result}
            size="medium"
            hasBackground={false}
            shouldFetchThumbnail
          />
        ),
        title: result.result.title,
        subtitle:
          result.type === ResultType.CalendarEvent
            ? getCalendarSubtitle(result.result)
            : getModifiedTimeAgo(result.result),
      };
    case ResultType.Recommendation:
      return {
        icon: (
          <FileContentIconWithThumbnail
            content={result.result}
            size="medium"
            hasBackground={false}
            shouldFetchThumbnail
          />
        ),
        title: result.result.title,
        subtitle: getModifiedTimeAgo(result.result),
      };
    case ResultType.MyProfileSuggestion: {
      const profileHref = result.result.displayHandle
        ? "dash.ai" + getUserProfilePageHref(result.result.displayHandle)
        : undefined;
      return {
        icon: (
          <AvatarIcon
            url={result.result.profilePhotoUrl}
            name={result.result.displayName}
          />
        ),
        title: i18n.t("omnibox_title_my_profile"),
        subtitle: profileHref ? profileHref : result.result.role,
      };
    }
    case ResultType.PeopleRecommendation: {
      const subtitle =
        result.result.role && result.result.type === "Contingent"
          ? i18n.t("omnibox_subtitle_role", { role: result.result.role })
          : result.result.role || "";
      return {
        icon: (
          <AvatarIcon
            url={result.result.profilePhotoUrl}
            name={result.result.displayName}
          />
        ),
        title: result.result.displayName || result.result.email,
        subtitle: subtitle,
      };
    }
    case ResultType.AskSuggestion:
      return {
        icon: (
          <UIIcon
            src={Twinkle2Line}
            size={"medium"}
            color={"var(--dig-color__text__base)"}
          />
        ),
        title: result.result.text!,
        subtitle: "",
      };
    case ResultType.RecentChat:
      return {
        icon: (
          <UIIcon
            src={ClockLine}
            size={"medium"}
            color={"var(--dig-color__text__base)"}
          />
        ),
        title: result.result.title,
        subtitle: "",
      };
    case ResultType.SearchFilter:
      return {
        ...getTypeaheadFilterDisplayProps(query, result.result),
      };
    case ResultType.MathCalculation:
      return {
        icon: <UIIcon src={CalculatorLine} />,
        title: (
          <Highlighter
            title={`${query.trim().replace(/\s+/g, " ")} = ${
              result.result.answer
            }`}
            highlightWords={[`= ${result.result.answer}`]}
          />
        ),
        subtitle: i18n.t("math_calculation_byline"),
      };
    case ResultType.Skeleton: {
      return {
        icon: (
          <IconLayout
            content={<Skeleton.Avatar size="small" />}
            shouldConstrainContent={false}
            size="small"
            shapeVariant="circle"
          />
        ),
        title: (
          <Skeleton.Text
            size="large"
            width={330}
            withBorderRadius="xsmall"
            borderRadius="XSmall"
            style={{ width: 330 }}
          />
        ),
        subtitle: (
          <Skeleton.Text
            size="large"
            width={180}
            withBorderRadius="xsmall"
            borderRadius="XSmall"
            style={{ width: 180 }}
          />
        ),
      };
    }
    default:
      // these all seem to never be passed in. i'm just enumerating them here
      result satisfies never;
      logger.warn(
        `getDisplayResult: Unsupported result type: ${(result as { type: UnsupportedResultTypes }).type}`,
      );
      return {} as RowDisplayResult;
  }
}
